/**
 * Modal para crear/editar prioridad
 * 
 * Referencia: sistema-tickets-frontend-COMPLETO.md Sección 5.5
 */

import React, { useState, useEffect, useContext } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControlLabel,
  Switch,
  Stack,
  Alert,
  CircularProgress,
  Box,
  InputAdornment
} from '@mui/material';
import axios from 'axios';
import { ErpContext } from '../../../context/ErpContext';
import { validarPrioridad } from '../../../utils/soporte/soporteValidations';

const ModalCrearPrioridad = ({ open, onClose, prioridad }) => {
  const { serverURL } = useContext(ErpContext);
  const esEdicion = !!prioridad;
  
  const [formData, setFormData] = useState({
    nombre: '',
    firstResponseHours: 8,
    resolutionHours: 72,
    color: '#1976d2',
    orden: 0,
    activa: true
  });
  
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  
  // Resetear formulario cuando cambia la prioridad o se abre/cierra el modal
  useEffect(() => {
    if (open) {
      if (prioridad) {
        // Modo edición: cargar datos de la prioridad
        setFormData({
          nombre: prioridad.nombre || '',
          firstResponseHours: prioridad.firstResponseHours || 8,
          resolutionHours: prioridad.resolutionHours || 72,
          color: prioridad.color || '#1976d2',
          orden: prioridad.orden || 0,
          activa: prioridad.activa !== undefined ? prioridad.activa : true
        });
      } else {
        // Modo creación: resetear formulario con valores por defecto
        setFormData({
          nombre: '',
          firstResponseHours: 8,
          resolutionHours: 72,
          color: '#1976d2',
          orden: 0,
          activa: true
        });
      }
      // Limpiar errores
      setErrors({});
      setError('');
    }
  }, [prioridad, open]);
  
  /**
   * Manejar cambio en campos del formulario
   */
  const handleChange = (field) => (event) => {
    let value = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
    
    // Convertir a número para campos numéricos
    if (field === 'firstResponseHours' || field === 'resolutionHours' || field === 'orden') {
      value = value === '' ? 0 : parseInt(value, 10);
      if (isNaN(value)) value = 0;
    }
    
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // Limpiar error del campo cuando el usuario empieza a escribir
    if (errors[field]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };
  
  /**
   * Validar formulario usando soporteValidations
   */
  const validar = () => {
    const validationErrors = validarPrioridad(formData);
    setErrors(validationErrors);
    return Object.keys(validationErrors).length === 0;
  };
  
  /**
   * Manejar envío del formulario
   */
  const handleSubmit = async () => {
    // Validar antes de enviar
    if (!validar()) {
      return;
    }
    
    setSaving(true);
    setError('');
    
    try {
      if (esEdicion) {
        // Editar prioridad existente
        await axios.patch(
          `${serverURL}/soporteAPI/prioridades/${prioridad.id}`,
          formData,
          { withCredentials: true }
        );
      } else {
        // Crear nueva prioridad
        await axios.post(
          `${serverURL}/soporteAPI/prioridades`,
          formData,
          { withCredentials: true }
        );
      }
      
      // Cerrar modal y recargar lista (pasar true para indicar que se debe recargar)
      onClose(true);
    } catch (err) {
      console.error('Error guardando prioridad:', err);
      setError(err.response?.data?.error || 'Error al guardar la prioridad');
    } finally {
      setSaving(false);
    }
  };
  
  /**
   * Manejar cierre del modal
   */
  const handleClose = () => {
    if (!saving) {
      onClose(false); // false = no recargar
    }
  };
  
  return (
    <Dialog 
      open={open} 
      onClose={handleClose} 
      maxWidth="sm" 
      fullWidth
      disableEscapeKeyDown={saving}
    >
      <DialogTitle>
        {esEdicion ? 'Editar Prioridad' : 'Nueva Prioridad'}
      </DialogTitle>
      
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}
        
        <Stack spacing={2} sx={{ mt: 1 }}>
          {/* Nombre */}
          <TextField
            fullWidth
            required
            label="Nombre"
            value={formData.nombre}
            onChange={handleChange('nombre')}
            error={!!errors.nombre}
            helperText={errors.nombre}
            disabled={saving}
            autoFocus
          />
          
          {/* First Response Hours */}
          <TextField
            fullWidth
            required
            type="number"
            label="Horas para Primera Respuesta"
            value={formData.firstResponseHours}
            onChange={handleChange('firstResponseHours')}
            error={!!errors.firstResponseHours}
            helperText={errors.firstResponseHours || 'Tiempo máximo en horas para dar la primera respuesta'}
            disabled={saving}
            inputProps={{ min: 1 }}
            InputProps={{
              endAdornment: <InputAdornment position="end">horas</InputAdornment>
            }}
          />
          
          {/* Resolution Hours */}
          <TextField
            fullWidth
            required
            type="number"
            label="Horas para Resolución"
            value={formData.resolutionHours}
            onChange={handleChange('resolutionHours')}
            error={!!errors.resolutionHours}
            helperText={errors.resolutionHours || 'Tiempo máximo en horas para resolver el ticket'}
            disabled={saving}
            inputProps={{ min: 1 }}
            InputProps={{
              endAdornment: <InputAdornment position="end">horas</InputAdornment>
            }}
          />
          
          {/* Color */}
          <Box>
            <TextField
              fullWidth
              label="Color"
              type="color"
              value={formData.color}
              onChange={handleChange('color')}
              error={!!errors.color}
              helperText={errors.color || 'Color que se mostrará en la interfaz para esta prioridad'}
              disabled={saving}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Box
                      sx={{
                        width: 32,
                        height: 32,
                        borderRadius: '4px',
                        bgcolor: formData.color,
                        border: '1px solid',
                        borderColor: 'divider',
                        mr: 1
                      }}
                    />
                  </InputAdornment>
                )
              }}
            />
            <TextField
              fullWidth
              sx={{ mt: 1 }}
              label="Código Hexadecimal"
              value={formData.color}
              onChange={handleChange('color')}
              error={!!errors.color}
              helperText="Formato: #RRGGBB (ej: #F44336)"
              disabled={saving}
              placeholder="#1976d2"
            />
          </Box>
          
          {/* Orden */}
          <TextField
            fullWidth
            type="number"
            label="Orden"
            value={formData.orden}
            onChange={handleChange('orden')}
            error={!!errors.orden}
            helperText={errors.orden || 'Orden de visualización (menor número = aparece primero)'}
            disabled={saving}
            inputProps={{ min: 0 }}
          />
          
          {/* Activa */}
          <FormControlLabel
            control={
              <Switch
                checked={formData.activa}
                onChange={handleChange('activa')}
                disabled={saving}
              />
            }
            label="Prioridad activa"
          />
        </Stack>
      </DialogContent>
      
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button 
          onClick={handleClose} 
          disabled={saving}
          color="inherit"
        >
          Cancelar
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={saving}
          startIcon={saving ? <CircularProgress size={16} /> : null}
        >
          {saving ? 'Guardando...' : esEdicion ? 'Actualizar' : 'Crear'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ModalCrearPrioridad;

